import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { EditIcon, PlusIcon } from "lucide-react";
import React from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

import * as schemas from "@blis/shared/schemas/process";
import { trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import type { ProcessTemplateDetail } from "../../types";
import { usePersistedAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";

type UpdateTemplateFormValues = z.infer<typeof schemas.updateTemplate>;

export function ProcessTemplateDetails({
  template,
}: {
  template: ProcessTemplateDetail;
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const navigate = useNavigate();

  const updateTemplate = trpc.admin.process.updateTemplate.useMutation(
    mutationResponse({
      errorTitle: "Failed to update process template",
      successMessage: "Process template updated",
      onSuccess: () => setIsEditing(false),
    }),
  );

  const handleCreateVersion = React.useCallback(() => {
    navigate({
      to: "/admin/processes/$id/versions/$versionId",
      params: { id: template.id, versionId: "new" },
    });
  }, [navigate, template.id]);

  const { options } = usePersistedAdminGridOptions(
    `process-versions-${template.id}`,
    template.versions,
    [
      {
        field: "version",
        headerName: "Version",
        cellRenderer: (params) => `Version ${params.value}`,
        width: 120,
      },
      {
        colId: "status",
        headerName: "Status",
        cellRenderer: (params) => {
          const isLatest = params.data.id === template.versions[0]?.id;
          return isLatest ? <Badge variant="soft">Latest</Badge> : null;
        },
        width: 100,
      },
      {
        field: "createdAt",
        headerName: "Created",
        cellRenderer: (params) => new Date(params.value).toLocaleDateString(),
        width: 120,
      },
      {
        colId: "actions",
        headerName: "Actions",
        cellRenderer: (params) => (
          <Button
            variant="ghost"
            size="sm"
            onPress={() => {
              navigate({
                to: "/admin/processes/$id/versions/$versionId",
                params: {
                  id: template.id,
                  versionId: params.data.id.toString(),
                },
              });
            }}
          >
            <EditIcon className="size-4" />
            Edit
          </Button>
        ),
        width: 100,
      },
    ],
    {
      sideBar: undefined,
      domLayout: "autoHeight",
      suppressHorizontalScroll: true,
      defaultColDef: {
        flex: 1,
      },
    },
  );

  const form = useForm<UpdateTemplateFormValues>({
    defaultValues: {
      id: template.id,
      name: template.name,
      description: template.description || undefined,
    },
    resolver: zodResolver(schemas.updateTemplate),
  });

  const onSubmit: SubmitHandler<UpdateTemplateFormValues> = (values) => {
    updateTemplate.mutate(values);
  };

  const handleCancel = () => {
    form.reset();
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* Template Information */}
      <div className="bg-accent-muted rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <Heading level={3}>Template</Heading>
          {!isEditing && (
            <Button
              variant="outline"
              size="sm"
              onPress={() => setIsEditing(true)}
            >
              <EditIcon className="size-4 mr-2" />
              Edit Template
            </Button>
          )}
        </div>

        {isEditing ? (
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                control={form.control}
                name="name"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    isInvalid={!!error}
                    validationBehavior="aria"
                  >
                    <Label>Template Name</Label>
                    <Input />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />

              <Controller
                control={form.control}
                name="description"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    isInvalid={!!error}
                    validationBehavior="aria"
                  >
                    <Label>Description</Label>
                    <Input />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                isPending={updateTemplate.isPending}
                isDisabled={updateTemplate.isPending}
              >
                Save Changes
              </Button>
              <Button
                variant="outline"
                onPress={handleCancel}
                isDisabled={updateTemplate.isPending}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="block text-sm font-medium text-muted-foreground">
                Name
              </Label>
              <Text className="block mt-2">{template.name}</Text>
            </div>
            <div>
              <Label className="block text-sm font-medium text-muted-foreground">
                Description
              </Label>
              <Text className="block mt-2">
                {template.description || "No description"}
              </Text>
            </div>
          </div>
        )}
      </div>

      {/* Versions Table */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <Heading level={3}>Versions</Heading>
          <Button size="sm" onPress={handleCreateVersion}>
            <PlusIcon className="size-4 mr-2" />
            Create New Version
          </Button>
        </div>

        <AdminGrid {...options} />
      </div>
    </div>
  );
}
